import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  FilePlus,
  FileText,
  Link,
  Trash2,
  CloudUpload,
  Upload,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  AlertTriangle,
  ArrowRight,
  Search,
  Filter,
  Calendar,
  Plus,
  Globe,
  Folder,
  Sparkles
} from "lucide-react";
import toast from "react-hot-toast";
import ReactMarkdown from "react-markdown";
import ApiService from "../../services/ApiService";
import useBillingStore from "../../store/useBillingStore";
import { useConfirmDialog } from "../ui/Alert";

function KnowledgeVault() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(false);
  const { billingInfo, fetchBillingInfo } = useBillingStore();
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [mainSitemapUrls, setMainSitemapUrls] = useState([]);
  const [queuing, setQueuing] = useState(false);
  const [groupedUrls, setGroupedUrls] = useState([]);
  const [groupExpanded, setGroupExpanded] = useState({});
  const [sitemapExpanded, setSitemapExpanded] = useState({});
  const [selectedUrls, setSelectedUrls] = useState(new Set());
  const [hasDiscovered, setHasDiscovered] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: "",
    contentType: "",
    content: "",
    file: null,
  });

  const [selectedVault, setSelectedVault] = useState(null);
  const [contentOpen, setContentOpen] = useState(false);
  const [faqsOpen, setFaqsOpen] = useState(false);
  const [loadingSpecificContent, setLoadingSpecificContent] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedContentType, setSelectedContentType] = useState("");
  const [selectedDateFilter, setSelectedDateFilter] = useState("");
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);


  const [jobStatus, setJobStatus] = useState(null)
  const [jobId, setJobId] = useState(null);
  const [jobVisible, setJobVisible] = useState(false);
  const [polling, setPolling] = useState(false);
  const [shownFailures, setShownFailures] = useState(new Set());

  const [showManualAdd, setShowManualAdd] = useState(false);
  const [manualSitemapUrl, setManualSitemapUrl] = useState('');
  const [loadingManualSitemap, setLoadingManualSitemap] = useState(false);
  const { confirm } = useConfirmDialog();
  const mismatchHandledRef = useRef(null);

  const handleQueueSelectedUrl = async () => {
    if (selectedUrls.size === 0) {
      toast.error("Please select at least one URL");
      return;
    }

    setQueuing(true);
    try {
      const urlList = Array.from(selectedUrls);
      const result = await ApiService.createCloudTasks(urlList);

      if (result?.success || result?.job_id) {
        toast.success("Successfully queued URLs for processing");
        setJobId(result.job_id);
        setJobVisible(true);
        setPolling(true);
        setSelectedUrls(new Set());
        setShowImportModal(false);
      } else {
        toast.error(result?.message || "Failed to queue URLs");
      }
    } catch (error) {
      console.error("Error queuing URLs:", error);
      toast.error("An error occurred while queuing URLs");
    } finally {
      setQueuing(false);
    }
  };

  const discoverSitemapFromUrl = async (sitemapUrl) => {
    try {
      const data = await ApiService.fetchSitemapURLs(sitemapUrl)

      if (data && data?.success) {
        const flattened = flattenSitemap(data.urls || {});
        const groups = {};
        flattened.forEach((u) => {
          const g = getGroupName(u);
          if (!groups[g]) groups[g] = [];
          groups[g].push(u);
        });
        const sortedGroups = Object.fromEntries(
          Object.keys(groups)
            .sort()
            .map((k) => [k, groups[k].sort((a, b) => toDisplayPath(a).localeCompare(toDisplayPath(b)))])
        );
        setGroupedUrls(prv => [sortedGroups, ...prv]);
        setSitemapExpanded(prev => ({ ...prev, [0]: !prev[0] }));
      } else {
        toast.error("Failed to add sitemap url");
      }

    } catch (error) {
      console.error('Error discovering sitemap:', error);
      throw error;
    }
  };


  const handleManualSitemapSubmit = async () => {
    if (!manualSitemapUrl.trim()) return;

    setLoadingManualSitemap(true);
    try {
      await discoverSitemapFromUrl(manualSitemapUrl.trim());
      const title = getTitleFromUrl(manualSitemapUrl);

      setMainSitemapUrls(prv => [{
        status: "done",
        content_id: crypto.randomUUID(),
        title: title,
        content_type: "url",
        original_url: manualSitemapUrl,
        filename: null,
        word_count: title.split(" ").length,
        created_at: new Date().toISOString()
      }, ...prv])
      setManualSitemapUrl('');
      setShowManualAdd(false);
    } catch (error) {
      console.error('Error loading manual sitemap:', error);
    } finally {
      setLoadingManualSitemap(false);
    }
  };

  const filteredGroupedUrls = useMemo(() => {
    if (!searchQuery.trim()) return groupedUrls;

    const query = searchQuery.toLowerCase();
    const filtered = {};
    let allGroupObject = {};
    for (let i = 0; i < groupedUrls?.length; i++) {
      allGroupObject = { ...allGroupObject, ...groupedUrls[i] }
    }

    Object.keys(allGroupObject).forEach(group => {
      const matchingUrls = allGroupObject[group].filter(url =>
        url.toLowerCase().includes(query) ||
        group.toLowerCase().includes(query)
      );

      if (matchingUrls.length > 0) {
        filtered[group] = matchingUrls;
      }
    });

    return filtered;
  }, [groupedUrls, searchQuery]);

  const handleDeleteSelectedUrls = () => {
    const urlsToDelete = new Set(selectedUrls);

    const updatedGroupedUrls = groupedUrls.map(sitemapGroups => {
      const updatedSitemap = {};

      Object.keys(sitemapGroups).forEach(group => {
        const remainingUrls = sitemapGroups[group].filter(url => !urlsToDelete.has(url));

        if (remainingUrls.length > 0) {
          updatedSitemap[group] = remainingUrls;
        }
      });

      return updatedSitemap;
    }).filter(sitemapGroups => Object.keys(sitemapGroups).length > 0);

    setGroupedUrls(updatedGroupedUrls);
    setSelectedUrls(new Set());
  };

  const totalUrls = useMemo(() => {
    if (Array.isArray(filteredGroupedUrls)) {
      return filteredGroupedUrls.reduce((sum, sitemapGroups) => {
        return sum + Object.values(sitemapGroups).reduce((groupSum, urls) => groupSum + urls.length, 0);
      }, 0);
    } else {
      return Object.values(filteredGroupedUrls).reduce((sum, urls) => sum + urls.length, 0);
    }
  }, [filteredGroupedUrls]);

  useEffect(() => {
    localStorage.setItem('loading', loading);
  }, [loading])

  const usageLimitReached = billingInfo &&
    billingInfo.limits &&
    billingInfo.usage &&
    (billingInfo.usage.uploads_count >= billingInfo.limits.uploads_count ||
      billingInfo.usage.total_file_size >= billingInfo.limits.total_file_size ||
      billingInfo.usage.tokens >= billingInfo.limits.tokens);

  const getUsageLimitMessage = () => {
    if (!billingInfo || !billingInfo.limits || !billingInfo.usage) return '';

    if (billingInfo.usage.tokens >= billingInfo.limits.tokens) {
      return 'You have exceeded your token usage limit.';
    }
    if (billingInfo.usage.uploads_count >= billingInfo.limits.uploads_count) {
      return 'You have exceeded your file upload count limit.';
    }
    if (billingInfo.usage.total_file_size >= billingInfo.limits.total_file_size) {
      return 'You have exceeded your total file size limit.';
    }
    return 'Usage limit exceeded.';
  };

  const getExceededDetails = () => {
    if (!billingInfo || !billingInfo.limits || !billingInfo.usage) return [];
    const details = [];
    const tokensUsed = billingInfo.usage?.tokens;
    const tokensLimit = billingInfo.limits?.tokens;
    if (typeof tokensUsed === 'number' && typeof tokensLimit === 'number' && tokensUsed >= tokensLimit) {
      details.push({ key: 'tokens', title: 'AI Tokens', used: tokensUsed, limit: tokensLimit, why: 'AI token quota exhausted — AI processing and optimizations are paused. Upgrade to continue AI processing.' });
    }
    const uploadsUsed = billingInfo.usage?.uploads_count;
    const uploadsLimit = billingInfo.limits?.uploads_count;
    if (typeof uploadsUsed === 'number' && typeof uploadsLimit === 'number' && uploadsUsed >= uploadsLimit) {
      details.push({ key: 'uploads_count', title: 'File Uploads', used: uploadsUsed, limit: uploadsLimit, why: 'You have reached your file upload count limit — you cannot upload more files. Upgrade to increase upload allowance.' });
    }
    const bytesUsed = billingInfo.usage?.total_file_size;
    const bytesLimit = billingInfo.limits?.total_file_size;
    if (typeof bytesUsed === 'number' && typeof bytesLimit === 'number' && bytesUsed >= bytesLimit) {
      details.push({ key: 'total_file_size', title: 'Upload Bytes', used: bytesUsed, limit: bytesLimit, why: 'Your total uploaded data size limit has been reached — please upgrade to add more storage and continue uploading.' });
    }
    return details;
  };

  const getGroupName = (urlStr) => {
    try {
      const u = new URL(urlStr);
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts.length === 0) return 'Root';
      const first = parts[0];
      return first.charAt(0).toUpperCase() + first.slice(1);
    } catch {
      const path = urlStr.startsWith('http') ? '/' : urlStr;
      const parts = path.split('/').filter(Boolean);
      if (parts.length === 0) return 'Root';
      const first = parts[0];
      return first.charAt(0).toUpperCase() + first.slice(1);
    }
  };

  const toDisplayPath = (urlStr) => {
    try {
      const u = new URL(urlStr);
      return u.pathname || '/';
    } catch {
      return urlStr;
    }
  };

  const normalizePath = (urlStr) => {
    try {
      const u = new URL(urlStr);
      const p = u.pathname || '/';
      return p.replace(/\/+$/, '') || '/';
    } catch {
      const p = (urlStr || '/');
      return p.replace(/\/+$/, '') || '/';
    }
  };

  const processedPathSet = useMemo(() => {
    const set = new Set();
    (content || []).forEach((item) => {
      if (item && item.original_url) {
        set.add(normalizePath(item.original_url));
      }
    });
    return set;
  }, [content]);

  const filteredContent = useMemo(() => {
    if (!content) return [];

    let filtered = content;

    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(item =>
        item.title?.toLowerCase().includes(query) ||
        item.name?.toLowerCase().includes(query) ||
        item.original_url?.toLowerCase().includes(query)
      );
    }

    if (selectedContentType && selectedContentType !== "") {
      filtered = filtered.filter(item =>
        item.content_type?.toLowerCase() === selectedContentType.toLowerCase()
      );
    }

    if (selectedDateFilter && selectedDateFilter !== "") {
      const now = new Date();
      const filterDate = new Date();

      if (selectedDateFilter === 'custom') {
        if (customDateFrom || customDateTo) {
          filtered = filtered.filter(item => {
            const itemDate = new Date(item.created_at);
            let passesFromDate = true;
            let passesToDate = true;

            if (customDateFrom) {
              const fromDate = new Date(customDateFrom);
              fromDate.setHours(0, 0, 0, 0);
              passesFromDate = itemDate >= fromDate;
            }

            if (customDateTo) {
              const toDate = new Date(customDateTo);
              toDate.setHours(23, 59, 59, 999);
              passesToDate = itemDate <= toDate;
            }

            return passesFromDate && passesToDate;
          });
        }
      } else {
        switch (selectedDateFilter) {
          case 'today':
            filterDate.setHours(0, 0, 0, 0);
            filtered = filtered.filter(item => {
              const itemDate = new Date(item.created_at);
              return itemDate >= filterDate;
            });
            break;
          case 'last7days':
            filterDate.setDate(now.getDate() - 7);
            filtered = filtered.filter(item => {
              const itemDate = new Date(item.created_at);
              return itemDate >= filterDate;
            });
            break;
          case 'last30days':
            filterDate.setDate(now.getDate() - 30);
            filtered = filtered.filter(item => {
              const itemDate = new Date(item.created_at);
              return itemDate >= filterDate;
            });
            break;
          case 'last90days':
            filterDate.setDate(now.getDate() - 90);
            filtered = filtered.filter(item => {
              const itemDate = new Date(item.created_at);
              return itemDate >= filterDate;
            });
            break;
          case 'thismonth':
            filterDate.setDate(1);
            filterDate.setHours(0, 0, 0, 0);
            filtered = filtered.filter(item => {
              const itemDate = new Date(item.created_at);
              return itemDate >= filterDate;
            });
            break;
          case 'thisyear':
            filterDate.setMonth(0, 1);
            filterDate.setHours(0, 0, 0, 0);
            filtered = filtered.filter(item => {
              const itemDate = new Date(item.created_at);
              return itemDate >= filterDate;
            });
            break;
          default:
            break;
        }
      }
    }

    return filtered;
  }, [content, searchQuery, selectedContentType, selectedDateFilter, customDateFrom, customDateTo]);

  const availableContentTypes = useMemo(() => {
    if (!content) return [];
    const types = new Set();
    content.forEach(item => {
      if (item.content_type) {
        types.add(item.content_type);
      }
    });
    return Array.from(types).sort();
  }, [content]);

  const isUrlProcessed = (urlStr) => processedPathSet.has(normalizePath(urlStr));

  const handleSelectAll = () => {
    const allUrls = new Set(selectedUrls);
    if (Array.isArray(filteredGroupedUrls)) {
      filteredGroupedUrls.forEach(sitemapGroups => {
        Object.values(sitemapGroups).forEach(urls => {
          urls.forEach(url => {
            if (!isUrlProcessed(url)) {
              allUrls.add(url);
            }
          });
        });
      });
    } else {
      Object.values(filteredGroupedUrls).forEach(urls => {
        urls.forEach(url => {
          if (!isUrlProcessed(url)) {
            allUrls.add(url);
          }
        });
      });
    }
    setSelectedUrls(allUrls);
  };

  const handleClearAll = () => {
    setSelectedUrls(new Set());
  };

  const handleDateFilterChange = (value) => {
    setSelectedDateFilter(value);
    if (value === 'custom') {
      setShowCustomDateInputs(true);
    } else {
      setShowCustomDateInputs(false);
      setCustomDateFrom("");
      setCustomDateTo("");
    }
  };

  const flattenSitemap = (node) => {
    const out = new Set();
    const walk = (obj) => {
      if (!obj || typeof obj !== 'object') return;
      for (const key of Object.keys(obj)) {
        const val = obj[key];
        if (Array.isArray(val)) {
          for (const url of val) out.add(url);
        } else if (val && typeof val === 'object') {
          walk(val);
        }
      }
    };
    walk(node);
    return Array.from(out);
  };

  useEffect(() => {
    if (filteredContent?.length) {
      const onlyUrls = filteredContent.filter(data => data?.content_type === 'url');
      setMainSitemapUrls(onlyUrls);
    }
  }, [filteredContent])

  const refreshSitemapDiscovery = async () => {
    try {
      setDiscovering(true);
      const data = await ApiService.fetchSitemapURLs();
      if (data && data.success) {
        const flattened = flattenSitemap(data.urls || {});
        const groups = {};
        flattened.forEach((u) => {
          const g = getGroupName(u);
          if (!groups[g]) groups[g] = [];
          groups[g].push(u);
        });
        const sortedGroups = Object.fromEntries(
          Object.keys(groups)
            .sort()
            .map((k) => [k, groups[k].sort((a, b) => toDisplayPath(a).localeCompare(toDisplayPath(b)))])
        );
        setGroupedUrls([sortedGroups]);
        if (!hasDiscovered) {
          const exp = {};
          Object.keys(sortedGroups).forEach((k, idx) => { exp[k] = idx < 3; });
          setGroupExpanded(exp);
        }
        setHasDiscovered(true);

        setSitemapExpanded(prev => ({ ...prev, [0]: !prev[0] }));
      } else {
        toast.error("Failed to discover sitemap URLs");
        setGroupedUrls([]);
      }
    } catch (_) {
      toast.error("Error discovering sitemap URLs");
      setGroupedUrls([]);
    } finally {
      setDiscovering(false);
    }
  };

  const openImportModal = async () => {
    resetVaultSelection();
    setShowImportModal(true);
    if (!hasDiscovered) {
      await refreshSitemapDiscovery();
    }
  };

  const toggleGroupExpand = (group) => {
    setGroupExpanded((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const toggleSitemapExpand = (index) => {
    setSitemapExpanded(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const isChecked = (url) => selectedUrls.has(url);
  const toggleUrl = (url) => {
    if (isUrlProcessed(url)) return;
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url); else next.add(url);
      return next;
    });
  };

  const groupAllChecked = (urls) => {
    const unprocessedUrls = urls.filter(u => !isUrlProcessed(u));
    return unprocessedUrls.length > 0 && unprocessedUrls.every(u => selectedUrls.has(u));
  };

  const toggleGroup = (urls) => {
    const unprocessedUrls = urls.filter(u => !isUrlProcessed(u));
    const allChecked = unprocessedUrls.every(u => selectedUrls.has(u));

    const newSelected = new Set(selectedUrls);
    if (allChecked) {
      unprocessedUrls.forEach(u => newSelected.delete(u));
    } else {
      unprocessedUrls.forEach(u => newSelected.add(u));
    }
    setSelectedUrls(newSelected);
  };

  useEffect(() => {
    loadContent();
    fetchBillingInfo();
  }, [fetchBillingInfo]);

  useEffect(() => {
    if (id) {
      const loadVaultFromId = async () => {
        const currentVaultId = selectedVault?.content_id || selectedVault?._id;
        if (currentVaultId === id && selectedVault.faqs) {
          return;
        }

        if (location.state?.initialVault) {
          const item = location.state.initialVault;
          const itemVaultId = item.content_id || item._id;
          if (itemVaultId === id) {
            setSelectedVault(item);
            await loadVaultDetails(id, item);
            return;
          }
        }

        let item = content.find(c => (c.content_id && c.content_id === id) || (c._id && c._id === id));

        if (selectedVault && (selectedVault.content_id === id || selectedVault._id === id)) {
          item = selectedVault;
        }

        if (item) {
          setSelectedVault(item);
          await loadVaultDetails(id, item);
        } else {
          await loadVaultDetails(id);
        }
      };

      loadVaultFromId();
    } else if (selectedVault) {
      resetVaultSelection(false);
    }
  }, [id, content.length]);

  useLayoutEffect(() => {
    if (selectedVault) {
      window.scrollTo(0, 0);
    }
  }, [selectedVault]);

  const loadContent = async () => {
    setLoading(true);
    try {
      const data = await ApiService.getContentList();
      setContent(data.content || []);

      if (data?.ongoing_job !== null) {
        setJobId(data?.ongoing_job);
        setJobVisible(false);
      }
    } catch (error) {
      toast.error("Failed to load the content");
    } finally {
      setLoading(false);
    }
  };

  const handleFAQNavigation = (question) => {
    navigate(`/optimization?search=${encodeURIComponent(question)}`);
  };

  const resetVaultSelection = (shouldNavigate = true) => {
    setSelectedVault(null);
    setFaqsOpen(false);
    setContentOpen(false);
    if (shouldNavigate && id) {
      navigate('/knowledge');
    }
  };

  const fetchSpecificContent = async (contentId) => {
    try {
      setLoadingSpecificContent(true);
      const response = await ApiService.getSpecificContent(contentId);
      return response;
    } catch (error) {
      console.error("Error fetching specific content:", error);
      toast.error("Failed to load detailed content");
      return null;
    } finally {
      setLoadingSpecificContent(false);
    }
  };

  const loadVaultDetails = async (contentId, expectedItem = null) => {
    setFaqsOpen(false);
    setContentOpen(false);

    const detailedContent = await fetchSpecificContent(contentId);
    if (detailedContent) {
      // Handle both new nested format ({ content: {...}, faqs: [...] }) and legacy flat format
      const isNestedContent = detailedContent.content && typeof detailedContent.content === 'object';
      const contentMetadata = isNestedContent ? detailedContent.content : detailedContent;
      const textContent = isNestedContent
        ? (detailedContent.content.content || "")
        : (typeof detailedContent.content === 'string' ? detailedContent.content : "");

      const normalizedVault = {
        ...contentMetadata,
        content: textContent,
        faqs: detailedContent.faqs || contentMetadata.faqs || []
      };

      if (expectedItem) {
        const urlMismatch = expectedItem.original_url && normalizedVault.original_url && expectedItem.original_url !== normalizedVault.original_url;
        const titleMismatch = expectedItem.title && normalizedVault.title && expectedItem.title !== normalizedVault.title;

        if (urlMismatch || titleMismatch) {
          if (mismatchHandledRef.current !== contentId) {
            toast.error("Data mismatch detected: The server returned different content for this ID.");
            mismatchHandledRef.current = contentId;
          }
          return;
        }
      }

      setSelectedVault(prev => ({
        ...prev,
        ...normalizedVault
      }));

      if (normalizedVault.faqs && normalizedVault.faqs.length > 0) {
        setFaqsOpen(true);
      }
    }
  };

  const handleKnowledgeVaultClick = async (item) => {
    const targetId = item.content_id || item._id;
    navigate(`/knowledge/${targetId}`, { state: { initialVault: item } });
  };

  function getTitleFromUrl(url) {
    try {
      const { pathname, hostname } = new URL(url);

      let text = pathname.replace(/\//g, " ").trim();

      if (!text) text = hostname.replace("www.", "");

      text = text
        .replace(/[-_]/g, " ")
        .replace(/\.\w+$/, "")
        .replace(/\d+/g, "")
        .trim();

      return text
        .split(" ")
        .filter(Boolean)
        .slice(0, 7)
        .map((word) => word[0].toUpperCase() + word.slice(1))
        .join(" ");
    } catch {
      return "";
    }
  }

  const handleUpload = async (e, isForm = true, url) => {
    e?.preventDefault && e.preventDefault();

    setUploading(true);

    if (usageLimitReached) {
      setUploading(false);
      toast.error(getUsageLimitMessage());
      return;
    }

    if (isForm) {
      if (uploadForm.contentType === "text" && !uploadForm.content) {
        toast.error("Please enter content");
        setUploading(false);
        return;
      }

      if (uploadForm.contentType === "pdf" && !uploadForm.file) {
        toast.error("Please select a PDF file");
        setUploading(false);
        return;
      }

      if (uploadForm.contentType === "docx" && !uploadForm.file) {
        toast.error("Please select a DOCX file");
        setUploading(false);
        return;
      }

      if (uploadForm.contentType === "csv" && !uploadForm.file) {
        toast.error("Please select a CSV file");
        setUploading(false);
        return;
      }

      if (uploadForm.contentType === "url" && !uploadForm.content) {
        toast.error("Please enter a URL");
        setUploading(false);
        return;
      } else if (uploadForm.contentType === "url" && uploadForm.content) {
        const isUrl = /^https:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/;
        if (!isUrl.test(uploadForm.content)) {
          toast.error("Please enter a valid url");
          setUploading(false);
          return;
        }
      }
    }

    try {
      const formData = new FormData();

      if (isForm) {
        formData.append("title", uploadForm.title);
        formData.append("content_type", uploadForm.contentType);

        if (
          (uploadForm.contentType === "pdf" ||
            uploadForm.contentType === "docx" ||
            uploadForm.contentType === "csv") &&
          uploadForm.file
        ) {
          formData.append("file", uploadForm.file);
        }

        else if (
          uploadForm.contentType === "text" ||
          uploadForm.contentType === "url"
        ) {
          formData.append("content", uploadForm.content);
        }
      } else {
        const title = getTitleFromUrl(url);
        formData.append('title', title);
        formData.append("content_type", "url");
        formData.append("content", url);
      }

      const result = await ApiService.uploadContent(formData);

      if (result.success) {
        toast.success(
          `Your content is being proccessed, please refresh after some time`
        );
        setShowUploadModal(false);
        setUploadForm({
          title: "",
          contentType: "text",
          content: "",
          file: null,
        });
        loadContent();
      } else {
        toast.error("Failed to upload content");
      }
    } catch (error) {
      toast.error("Something went wrong while proccessing your request");
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const extension = file.name.split('.').pop().toLowerCase();
      let detectedType = 'text';

      if (extension === 'pdf') detectedType = 'pdf';
      else if (extension === 'docx') detectedType = 'docx';
      else if (extension === 'csv') detectedType = 'csv';

      setUploadForm({
        ...uploadForm,
        file,
        contentType: detectedType,
        title: uploadForm.title || file.name.replace(/\.[^/.]+$/, '')
      });
    }
  };

  const handleContentChange = (e) => {
    const value = e.target.value;

    if (!value) {
      setUploadForm({
        title: "",
        contentType: "",
        content: "",
        file: null,
      })
    } else {
      if (e.target.type === 'url') {
        setUploadForm({
          ...uploadForm,
          content: value,
          contentType: 'url'
        });
      } else {
        setUploadForm({
          ...uploadForm,
          content: value,
          contentType: 'text'
        });
      }
    }

  };

  const clearFile = () => {
    setUploadForm({ ...uploadForm, file: null, contentType: '' });
  };

  const getContentTypeInfo = () => {
    const types = {
      text: { icon: FileText, label: 'Text Content', color: 'text-black', bg: 'bg-gray-100' },
      url: { icon: Link, label: 'Website URL', color: 'text-black', bg: 'bg-gray-100' },
      pdf: { icon: FileText, label: 'PDF File', color: 'text-black', bg: 'bg-gray-100' },
      docx: { icon: FileText, label: 'DOCX File', color: 'text-black', bg: 'bg-gray-100' },
      csv: { icon: FileText, label: 'CSV File', color: 'text-black', bg: 'bg-gray-100' }
    };
    return types[uploadForm.contentType] || types.text;
  };

  const typeInfo = getContentTypeInfo();
  const TypeIcon = typeInfo.icon;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e);
    }
  };

  const handleDelete = async (contentId) => {
    const results = await confirm({
      title: 'Delete FAQ Content',
      message: `Are you sure you want to delete this content? This will also remove all associated FAQs.`,
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });
    if (!results) {
      return;
    }

    try {
      const result = await ApiService.deleteContent(contentId);

      if (result.success) {
        toast.success("Content deleted successfully");
        loadContent();
      } else {
        toast.error("Failed to delete content");
      }
    } catch (error) {
      toast.error("Failed to delete content");
    }
  };

  const getContentIcon = (type) => {
    return FileText;
  };

  useEffect(() => {
    let intervalId;

    if (polling && jobId) {
      intervalId = setInterval(async () => {
        try {
          const result = await ApiService.trackJobProgress(jobId);
          setJobStatus(result);

          if (result?.job_status === "job_completed" || result?.job_status === "job_creation_failed" || result?.job_status === "job_force_closed") {
            setPolling(false);
          }

        } catch (err) {
          console.error("Error fetching job status:", err);
        }
      }, 5000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [polling, jobId]);

  useEffect(() => {
    if (jobStatus?.job_status === "job_completed" ||
      jobStatus?.job_status === "job_creation_failed" ||
      jobStatus?.job_status === "job_force_closed") {
      loadContent();
    }
  }, [jobStatus]);

  useEffect(() => {
    if (!jobStatus) return;

    const failed = jobStatus?.failed || 0;
    const currentFailures = jobStatus?.final_response?.failed_url_list || [];

    if (failed > 0 && currentFailures.length > 0) {
      currentFailures.forEach((item) => {
        const url = typeof item === 'string' ? item : item.url;
        const error = typeof item === 'object' ? item.error : 'Unknown error';

        if (!shownFailures.has(url)) {
          setShownFailures(prev => new Set([...prev, url]));

          toast.error(
            <div className="flex flex-col gap-1">
              <p className="font-semibold text-sm">URL Failed to Process</p>
              <p className="text-xs text-gray-700 break-all">{url}</p>
              {error && <p className="text-xs text-red-600">Reason: {error}</p>}
            </div>,
            {
              duration: 6000,
              icon: '❌',
            }
          );
        }
      });
    }
  }, [jobStatus, shownFailures]);


  const handleStopJob = async () => {
    if (!jobId) {
      toast.error("Failed to delete: Job ID is missing")
      return;
    }

    try {
      const result = await ApiService.stopCloudTasksJob(jobId);

      if (result?.success) {
        toast.success("Deleted Job successfuly");
      }

      else {
        toast.error(`An error occurred while deleting your Job: ${result?.message}`)
      }

    }

    catch (error) {
      toast.error(`An error occurred while deleting your Job: ${error}`);
    }
  }


  const isSearching = searchQuery.trim().length > 0;

  const renderProgressBar = () => {
    if (!jobStatus) return null;

    const message = jobStatus?.job_status_message || "Processing job...";
    const success = jobStatus?.success || 0;
    const pending = jobStatus?.pending || 0;
    const failed = jobStatus?.failed || 0;
    const total = success + failed + pending || 1;

    const successPercent = ((success / total) * 100).toFixed(0);
    const failedPercent = ((failed / total) * 100).toFixed(0);
    const pendingPercent = 100 - successPercent - failedPercent;

    const completedPercent = ((success + failed) / total * 100).toFixed(0);

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-black/5 rounded-full w-fit">
          <Sparkles className="w-3.5 h-3.5 text-black" />
          <span className="text-[10px] font-bold text-black uppercase tracking-wider">AI is categorizing your content</span>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between items-center gap-3 flex-wrap sm:flex-nowrap">
            <p className="text-gray-700 font-medium text-sm flex-shrink min-w-0">{message}</p>
            <p className="text-sm font-bold text-gray-900 whitespace-nowrap flex-shrink-0">
              {completedPercent}% Complete
            </p>
          </div>
          <div className="relative w-full h-10 bg-gray-100 rounded-xl overflow-hidden flex shadow-sm border border-gray-200">
            <div
              style={{ width: `${successPercent}%` }}
              className="bg-gradient-to-r from-black to-gray-800 flex items-center justify-center transition-all duration-500 ease-out"
            >
              {successPercent > 10 && (
                <span className="text-xs font-bold text-white drop-shadow-md flex items-center gap-1">
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {successPercent}%
                </span>
              )}
            </div>
            <div
              style={{ width: `${failedPercent}%` }}
              className="bg-gradient-to-r from-red-500 to-red-600 flex items-center justify-center transition-all duration-500 ease-out"
            >
              {failedPercent > 10 && (
                <span className="text-xs font-bold text-white drop-shadow-md flex items-center gap-1">
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  {failedPercent}%
                </span>
              )}
            </div>
            <div
              style={{ width: `${pendingPercent}%` }}
              className="bg-gradient-to-r from-gray-300 to-gray-400 flex items-center justify-center transition-all duration-500 ease-out"
            >
              {pendingPercent > 10 && (
                <span className="text-xs font-bold text-gray-700 drop-shadow-sm flex items-center gap-1">
                  <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {pendingPercent}%
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-black to-gray-800 text-white rounded-xl p-3 shadow-md">
            <div className="flex items-center gap-2 mb-1">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-xs font-medium opacity-90">Success</span>
            </div>
            <p className="text-2xl font-bold">{success}</p>
          </div>

          <div className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl p-3 shadow-md">
            <div className="flex items-center gap-2 mb-1">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              <span className="text-xs font-medium opacity-90">Failed</span>
            </div>
            <p className="text-2xl font-bold">{failed}</p>
          </div>

          <div className="bg-gradient-to-br from-gray-300 to-gray-400 text-gray-800 rounded-xl p-3 shadow-md">
            <div className="flex items-center gap-2 mb-1">
              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-xs font-medium">Pending</span>
            </div>
            <p className="text-2xl font-bold">{pending}</p>
          </div>

          <div className="bg-gradient-to-br from-gray-100 to-gray-200 text-gray-900 rounded-xl p-3 shadow-md border-2 border-gray-300">
            <div className="flex items-center gap-2 mb-1">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
              </svg>
              <span className="text-xs font-semibold">Total</span>
            </div>
            <p className="text-2xl font-bold">{total}</p>
          </div>
        </div>
      </div>
    );
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[90vh]">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            Knowledge Vault
          </h2>
          <p className="text-gray-600 mt-0.5 max-w-2xl text-sm sm:text-base">
            Upload domain-specific content to automatically generate FAQs and
            enhance your AI knowledge base
          </p>
        </div>
        <div className="flex w-full md:w-auto flex-col md:flex-row gap-2 md:justify-end items-stretch md:items-center">
          <button
            onClick={() => {
              setSelectedVault(null);
              setShowUploadModal(true);
            }}
            className="inline-flex h-10 items-center justify-center w-full md:w-auto md:min-w-[160px] px-4 text-sm bg-black text-white font-medium rounded-lg shadow-lg hover:shadow-xl hover:bg-gray-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FilePlus className="h-4 w-4 mr-2" />
            Upload Content
          </button>
          <button
            onClick={() => {
              openImportModal();
            }}
            className="inline-flex h-10 items-center justify-center w-full md:w-auto md:min-w-[160px] px-4 text-sm bg-black text-white font-medium rounded-lg shadow-lg hover:shadow-xl hover:bg-gray-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Link className="h-4 w-4 mr-2" />
            Import Website
          </button>
        </div>
      </div>

      {(() => {
        const exceeded = getExceededDetails();
        if (!exceeded || exceeded.length === 0) return null;
        return (
          <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50/60 backdrop-blur p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <div className="h-9 w-9 rounded-lg bg-gray-100 flex items-center justify-center border border-gray-200">
                  <AlertTriangle className="h-5 w-5 text-black" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-black">
                      Usage limit reached
                    </h4>
                    <p className="text-sm text-gray-700/90 mt-0.5">
                      You&apos;ve reached the following monthly quota{exceeded.length > 1 ? 's' : ''}. Upgrade to increase limits and continue uploading/importing content.
                    </p>
                  </div>
                  <a
                    href="/billing"
                    className="inline-flex items-center gap-2 rounded-lg bg-black text-white px-3 py-2 text-sm font-semibold shadow-sm hover:bg-gray-800 transition-colors"
                  >
                    View billing & upgrade
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {exceeded.map((d) => {
                    const percent = d.limit > 0 ? Math.min(100, Math.round((d.used / d.limit) * 100)) : 100;
                    return (
                      <div key={d.key} className="rounded-lg border border-gray-200 bg-white/60 p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium uppercase tracking-wide text-gray-700/80">
                            {d.title}
                          </span>
                          <span className="text-[11px] font-semibold text-black bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">
                            {d.used} / {d.limit}
                          </span>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-gray-100 border border-gray-200 overflow-hidden">
                          <div
                            className="h-full bg-black"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <p className="mt-2 text-xs text-gray-700/90 leading-relaxed">
                          {d.why}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {content && content.length > 0 && (
        <div className="space-y-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search Knowledge Vault..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5 focus:border-black transition-colors duration-200 text-sm"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative min-w-[200px]">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Filter className="h-4 w-4 text-gray-400" />
                </div>
                <select
                  value={selectedContentType}
                  onChange={(e) => setSelectedContentType(e.target.value)}
                  className="block w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-black/5 focus:border-black transition-colors duration-200 appearance-none bg-white text-sm"
                >
                  <option value="">All Content Types</option>
                  {availableContentTypes.map((type) => (
                    <option key={type} value={type}>
                      {type.toUpperCase()}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </div>
              </div>

              <div className="relative min-w-[180px]">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Calendar className="h-4 w-4 text-gray-400" />
                </div>
                <select
                  value={selectedDateFilter}
                  onChange={(e) => handleDateFilterChange(e.target.value)}
                  className="block w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-black/5 focus:border-black transition-colors duration-200 appearance-none bg-white text-sm"
                >
                  <option value="">Filter By Date</option>
                  <option value="today">Today</option>
                  <option value="last7days">Last 7 Days</option>
                  <option value="last30days">Last 30 Days</option>
                  <option value="last90days">Last 90 Days</option>
                  <option value="thismonth">This Month</option>
                  <option value="thisyear">This Year</option>
                  <option value="custom">Custom Range</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </div>
              </div>

              {(searchQuery || selectedContentType || selectedDateFilter) && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedContentType("");
                    setSelectedDateFilter("");
                    setCustomDateFrom("");
                    setCustomDateTo("");
                    setShowCustomDateInputs(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center gap-2 whitespace-nowrap"
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {showCustomDateInputs && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                  <input
                    type="date"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                    className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-colors duration-200"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                  <input
                    type="date"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                    className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-colors duration-200"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {jobStatus && jobVisible && (
        <div className="bg-gradient-to-br from-white to-gray-50 shadow-2xl rounded-2xl p-6 space-y-5 border border-gray-200 animate-in fade-in duration-300">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
            <div className="flex items-center gap-3">
              <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${jobStatus?.job_status === "job_completed"
                ? "bg-black"
                : jobStatus?.job_status === "job_creation_failed"
                  ? "bg-red-100"
                  : "bg-gray-100"
                }`}>
                {jobStatus?.job_status === "job_completed" ? (
                  <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : jobStatus?.job_status === "job_creation_failed" ? (
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black"></div>
                )}
              </div>
              <div>
                <h3 className="font-bold text-xl text-gray-900">
                  {jobStatus?.job_status === "job_completed"
                    ? "Import Complete!"
                    : jobStatus?.job_status === "job_creation_failed"
                      ? "Import Failed"
                      : "Importing Content..."}
                </h3>
                <p className="text-sm text-gray-600 mt-0.5">
                  {jobStatus?.job_status === "job_completed" || jobStatus?.job_status === "job_force_closed"
                    ? "You can now view the results or close this tracker."
                    : "Please wait while we process your content"}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {jobStatus?.job_status !== "job_creation_failed" &&
                jobStatus?.job_status !== "job_completed" && jobStatus?.job_status !== "job_force_closed" && (
                  <>
                    <button
                      onClick={() => setPolling(true)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium transition-all duration-200 hover:bg-gray-200 hover:shadow-md flex items-center gap-2"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh
                    </button>
                    <button
                      onClick={handleStopJob}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-xl text-sm font-medium transition-all duration-200 hover:bg-red-200 hover:shadow-md flex items-center gap-2"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Stop
                    </button>
                  </>
                )}

              {jobStatus?.job_status === "job_completed" ||
                jobStatus?.job_status === "job_creation_failed" ||
                jobStatus?.job_status === "job_force_closed" ? (
                <>
                  <button
                    onClick={() => setJobVisible(false)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium transition-all duration-200 hover:bg-gray-200 hover:shadow-md flex items-center gap-2"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    Minimize
                  </button>
                  <button
                    onClick={() => {
                      setJobVisible(false);
                      setPolling(false);
                      setJobStatus(null);
                      setJobId(null);
                    }}
                    className="px-4 py-2 bg-black text-white rounded-xl text-sm font-medium transition-all duration-200 hover:bg-gray-800 hover:shadow-md flex items-center gap-2"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Close
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setJobVisible(false)}
                  className="px-4 py-2 bg-black text-white rounded-xl text-sm font-medium transition-all duration-200 hover:bg-gray-800 hover:shadow-md flex items-center gap-2"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  Minimize
                </button>
              )}
            </div>
          </div>

          {renderProgressBar()}

          {(jobStatus?.job_status === "job_completed" || jobStatus?.job_status === "job_force_closed") &&
            jobStatus?.final_response?.failed_url_list?.length > 0 && (
              <div className="mt-4">
                <details className="bg-red-50 border-2 border-red-200 rounded-xl overflow-hidden">
                  <summary className="cursor-pointer font-semibold text-red-700 px-4 py-3 hover:bg-red-100 transition-colors flex justify-between items-center group">
                    <span className="flex items-center gap-2">
                      <svg className="h-5 w-5 transition-transform duration-200 group-open:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <span className="flex items-center gap-2">
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Failed URLs ({jobStatus.final_response.failed_url_list.length})
                      </span>
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const failedUrlsText = jobStatus.final_response.failed_url_list
                          .map(item => typeof item === 'string' ? item : `${item.url}: ${item.error}`)
                          .join('\n');
                        navigator.clipboard.writeText(failedUrlsText);
                        toast.success("Copied to clipboard!");
                      }}
                      className="text-xs px-3 py-1.5 bg-red-200 text-red-700 rounded-lg hover:bg-red-300 font-medium transition-colors"
                    >
                      Copy All
                    </button>
                  </summary>

                  <div className="px-4 py-3 space-y-2 max-h-64 overflow-y-auto">
                    {jobStatus.final_response.failed_url_list.map((item, i) => (
                      <div key={i} className="p-3 bg-white border border-red-200 rounded-lg hover:shadow-md transition-shadow">
                        <div className="flex items-start gap-2">
                          <svg className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 break-all">
                              {typeof item === 'string' ? item : item.url}
                            </p>
                            {typeof item === 'object' && item.error && (
                              <p className="text-xs text-red-600 mt-1 bg-red-50 px-2 py-1 rounded">
                                <span className="font-semibold">Error:</span> {item.error}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            )}

          {(jobStatus?.job_status === "job_completed" || jobStatus?.job_status === "job_force_closed") &&
            jobStatus?.final_response?.pending_url_list?.length > 0 && (
              <div className="mt-4">
                <details className="bg-gray-50 border-2 border-gray-200 rounded-xl overflow-hidden">
                  <summary className="cursor-pointer font-semibold text-gray-700 px-4 py-3 hover:bg-gray-100 transition-colors flex justify-between items-center group">
                    <span className="flex items-center gap-2">
                      <svg className="h-5 w-5 transition-transform duration-200 group-open:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <span className="flex items-center gap-2">
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Pending URLs ({jobStatus.final_response.pending_url_list.length})
                      </span>
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(
                          jobStatus.final_response.pending_url_list.join('\n')
                        );
                        toast.success("Copied to clipboard!");
                      }}
                      className="text-xs px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors"
                    >
                      Copy All
                    </button>
                  </summary>
                  <div className="px-4 py-3 space-y-2 max-h-64 overflow-y-auto">
                    {jobStatus.final_response.pending_url_list.map((url, i) => (
                      <div key={i} className="p-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 break-all hover:bg-gray-50 transition-colors">
                        {url}
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            )}
        </div>
      )}

      {jobId && !jobVisible && (
        <button
          onClick={() => {
            setJobVisible(true);
            setPolling(true);
          }}
          className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-black to-gray-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 flex items-center justify-center gap-3"
        >
          <div className="animate-pulse h-2 w-2 bg-white rounded-full"></div>
          <span>Track Progress of Your Job</span>
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      )}

      {selectedVault && !showImportModal ? (
        <div className="space-y-4">
          <div className="flex items-center text-sm text-gray-500 gap-2">
            <button
              onClick={() => resetVaultSelection()}
              className="flex items-center text-gray-600 hover:text-black transition gap-2"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="font-medium">Knowledge Vault</span>
            </button>
            <span className="text-gray-300">/</span>
            <span className="text-black font-semibold">{selectedVault.title}</span>
            <div className="ml-auto flex items-center gap-2">
              <span className="px-2.5 py-1 rounded-full bg-gray-100 text-black text-xs font-semibold uppercase tracking-wide">
                {selectedVault.content_type}
              </span>
              {loadingSpecificContent && (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                  <span className="text-xs text-gray-600">Loading details...</span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-5 border border-gray-200 space-y-4">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-xl md:text-2xl font-semibold text-black" style={{ textTransform: 'capitalize' }}>
                  {selectedVault.title}
                </h3>
                <p className="text-sm text-gray-500 mt-1 break-all">
                  {selectedVault.filename || selectedVault.original_url || ""}
                </p>
              </div>
              <div className="flex items-start md:items-center gap-6 text-sm text-gray-600">
                <div>
                  <span className="font-medium text-gray-800">Created:</span>{" "}
                  {new Date(selectedVault.created_at).toLocaleString()}
                </div>
                <div>
                  <span className="font-medium text-gray-800">Words:</span>{" "}
                  {selectedVault.word_count}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-100">
              <button
                onClick={() => setFaqsOpen(!faqsOpen)}
                className="w-full flex items-center justify-between px-4 md:px-5 py-3 text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-black rounded-md text-white">
                    <FilePlus className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">FAQs</div>
                    <div className="text-xs text-gray-500">
                      {loadingSpecificContent ? (
                        "Loading..."
                      ) : (
                        `${selectedVault.faqs?.length || 0} generated`
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-gray-500">
                  {faqsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
              </button>

              {faqsOpen && loadingSpecificContent && (
                <div className="p-4 md:p-5 border-t border-gray-100 text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                    <span className="text-sm text-gray-600">Loading FAQs and context...</span>
                  </div>
                </div>
              )}

              {faqsOpen && !loadingSpecificContent && selectedVault.faqs && selectedVault.faqs.length > 0 && (
                <div className="p-4 md:p-5 border-t border-gray-100 space-y-3">
                  {selectedVault.faqs.map((faq, index) => (
                    <div
                      key={`faq-${faq.faq_id || faq.slug || index}-${index}`}
                      onClick={() => handleFAQNavigation(faq.question)}
                      className="border border-gray-100 rounded-lg overflow-hidden cursor-pointer hover:bg-gray-50 transition"
                    >
                      <div className="w-full flex items-center justify-between px-4 md:px-5 py-3 text-left">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-800 line-clamp-2">
                            Q: {faq.question}
                          </div>
                          <div className="text-xs text-gray-500 line-clamp-1">{faq.context || ""}</div>
                        </div>
                      </div>

                      <div className="px-4 md:px-5 pb-4 text-gray-700">
                        <div className="text-sm">A: {faq.answer}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {faqsOpen && !loadingSpecificContent && (!selectedVault.faqs || selectedVault.faqs.length === 0) && (
                <div className="p-4 md:p-5 border-t border-gray-100 text-sm text-gray-500">
                  No FAQs generated for this content yet.
                </div>
              )}
            </div>

            {(selectedVault.content || selectedVault.word_count > 0 || loadingSpecificContent) && (
              <div className="bg-white rounded-lg border border-gray-100">
                <button
                  onClick={() => setContentOpen(!contentOpen)}
                  className="w-full flex items-center justify-between px-4 md:px-5 py-3 text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-black rounded-md text-white">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">Context</div>
                      <div className="text-xs text-gray-500">
                        {loadingSpecificContent ? (
                          "Loading content..."
                        ) : (
                          `${Math.min(200, selectedVault.content?.length || 0)} chars preview`
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-gray-500">
                    {contentOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </button>

                {contentOpen && (
                  <div
                    className="p-4 md:p-5 border-t border-gray-100 text-gray-700 prose max-w-none"
                    style={{ maxHeight: "55vh", overflowY: "auto" }}
                  >
                    {loadingSpecificContent ? (
                      <div className="text-center py-8">
                        <div className="flex items-center justify-center space-x-2">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black"></div>
                          <span className="text-gray-600">Loading full content...</span>
                        </div>
                      </div>
                    ) : selectedVault.content ? (
                      <ReactMarkdown>{selectedVault.content}</ReactMarkdown>
                    ) : (
                      <div className="text-gray-500 italic py-4">
                        {selectedVault.word_count > 0
                          ? "Content is being processed or failed to load. Please try refreshing."
                          : "No content available for this item."}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : content.length === 0 ? (
        <div className="text-center py-12 md:py-16">
          <div className="relative mx-auto w-20 h-20 md:w-24 md:h-24 mb-6">
            <div className="absolute inset-0 bg-gray-100 rounded-2xl"></div>
            <div className="absolute inset-2 bg-white rounded-xl flex items-center justify-center">
              <CloudUpload className="h-7 w-7 md:h-8 md:w-8 text-black" />
            </div>
          </div>
          <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">No content yet</h3>
          <p className="text-gray-500 mb-6 md:mb-8 max-w-md mx-auto">
            Get started by uploading your first piece of content. We support text, PDFs, DOCX, CSV files, and web URLs.
          </p>
          <button
            onClick={() => {
              resetVaultSelection();
              setShowUploadModal(true);
            }}
            className="inline-flex items-center px-6 py-3 bg-black text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:bg-gray-800 transition-all duration-200 transform hover:-translate-y-0.5"
          >
            <FilePlus className="h-5 w-5 mr-2" />
            Upload Content
          </button>
        </div>
      ) : filteredContent.length === 0 ? (
        <div className="text-center py-12 md:py-16">
          <div className="relative mx-auto w-20 h-20 md:w-24 md:h-24 mb-6">
            <div className="absolute inset-0 bg-gray-100 rounded-2xl"></div>
            <div className="absolute inset-2 bg-white rounded-xl flex items-center justify-center">
              <Search className="h-7 w-7 md:h-8 md:w-8 text-gray-500" />
            </div>
          </div>
          <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">No results found</h3>
          <p className="text-gray-500 mb-6 md:mb-8 max-w-md mx-auto">
            No content matches your search criteria. Try adjusting your search query or filters.
          </p>
          <button
            onClick={() => {
              setSearchQuery("");
              setSelectedContentType("");
              setSelectedDateFilter("");
              setCustomDateFrom("");
              setCustomDateTo("");
              setShowCustomDateInputs(false);
            }}
            className="inline-flex items-center px-6 py-3 bg-gray-100 text-black font-medium rounded-xl shadow-lg hover:shadow-xl hover:bg-gray-200 transition-all duration-200 transform hover:-translate-y-0.5"
          >
            <X className="h-5 w-5 mr-2" />
            Clear Filters
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {(searchQuery || selectedContentType || selectedDateFilter) && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {filteredContent.length} of {content.length} items
                {searchQuery && (
                  <span className="ml-1">
                    matching &quot;<span className="font-medium">{searchQuery}</span>&quot;
                  </span>
                )}
                {selectedContentType && (
                  <span className="ml-1">
                    in <span className="font-medium">{selectedContentType.toUpperCase()}</span>
                  </span>
                )}
                {selectedDateFilter && (
                  <span className="ml-1">
                    from <span className="font-medium">
                      {selectedDateFilter === 'today' && 'Today'}
                      {selectedDateFilter === 'last7days' && 'Last 7 Days'}
                      {selectedDateFilter === 'last30days' && 'Last 30 Days'}
                      {selectedDateFilter === 'last90days' && 'Last 90 Days'}
                      {selectedDateFilter === 'thismonth' && 'This Month'}
                      {selectedDateFilter === 'thisyear' && 'This Year'}
                      {selectedDateFilter === 'custom' && (
                        <>
                          {customDateFrom && customDateTo ? `${customDateFrom} to ${customDateTo}` :
                            customDateFrom ? `${customDateFrom} onwards` :
                              customDateTo ? `up to ${customDateTo}` :
                                'Custom Range'}
                        </>
                      )}
                    </span>
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredContent.map((item, index) => {
              const Icon = getContentIcon(item.content_type);
              return (
                <div
                  key={`${item.content_id || item._id || index}-${index}`}
                  onClick={() => {
                    handleKnowledgeVaultClick(item);
                  }}
                  className="cursor-pointer group bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md hover:border-black transition-all duration-200"
                >
                  <div className="p-4 sm:p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="p-1.5 bg-black rounded-lg mr-2 group-hover:scale-110 transition-transform duration-200">
                          <Icon className="h-4 w-4 text-white" />
                        </div>
                        <span className="text-xs uppercase tracking-wide text-black font-semibold bg-gray-100 px-2 py-1 rounded-full">
                          {item.content_type}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(item.content_id || item._id);
                        }}
                        className="text-gray-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-all duration-200"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    {item.original_url && (
                      <div className="mb-2">
                        <div
                          className="text-[10px] text-gray-500 break-all leading-tight block bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-100"
                          title={item.original_url}
                        >
                          {item.original_url}
                        </div>
                      </div>
                    )}

                    <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-2 group-hover:text-black transition-colors duration-200 line-clamp-2" style={{ textTransform: 'capitalize' }}>
                      {item.title}
                    </h3>
                    <div className="space-y-2 mb-4">

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Created:</span>
                        <span className="text-gray-700">
                          {new Date(item.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    {item.content && (
                      <div className="text-xs text-gray-600 bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                        <div className="max-w-none text-gray-600 leading-relaxed line-clamp-3">
                          {item.content.length > 150
                            ? `${item.content.substring(0, 150)}...`
                            : item.content}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showUploadModal && (
        <div
          className="fixed inset-0 overflow-hidden z-50"
          style={{
            height: "100%",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowUploadModal(false)}
          ></div>

          <div className="flex items-center justify-center min-h-screen px-4 py-8 overflow-hidden">
            <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-100 w-full max-w-xl mx-auto max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 rounded-t-3xl flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 ${typeInfo.bg} rounded-xl`}>
                    <TypeIcon className={`h-5 w-5 ${typeInfo.color}`} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Upload Content</h2>
                    <p className="text-xs text-gray-500 mt-0.5">{typeInfo.label} detected</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleUpload} className="p-6 space-y-5 overflow-hidden">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Title
                    </label>
                    <span className="text-[10px] font-medium text-black bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">
                      Auto-generated if empty
                    </span>
                  </div>
                  <input
                    type="text"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-gray-500/20 focus:border-gray-500 transition-all duration-200 text-gray-900 placeholder:text-gray-400"
                    placeholder="Give your content a title (optional)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Content
                  </label>

                  {
                    !uploadForm.file && uploadForm.contentType !== 'text' && (
                      <div className="space-y-3">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Link className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="url"
                            value={uploadForm.content}
                            onChange={(e) => handleContentChange(e)}
                            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-gray-500/20 focus:border-gray-500 transition-all duration-200 text-gray-900 placeholder:text-gray-400"
                            placeholder="https://example.com/article"
                            required={uploadForm.contentType === 'url'}
                          />
                        </div>
                        {uploadForm.content && (
                          <div className="flex items-center gap-2 text-xs text-black bg-gray-100 px-3 py-2 rounded-lg">
                            <Link className="h-3.5 w-3.5" />
                            <span>We&apos;ll fetch the content from this website</span>
                          </div>
                        )}
                        
                        {/* Divider */}
                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300"></div>
                          </div>
                          <div className="relative flex justify-center">
                            <span className="px-4 py-1 bg-white text-gray-600 text-sm font-semibold tracking-wide rounded-full shadow-sm">
                              OR UPLOAD FILE
                            </span>
                          </div>
                        </div>
                      </div>
                    )
                  }

                  {
                    !uploadForm.content && (
                      !uploadForm.file ? (
                        <div className="space-y-4">
                          <div
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                            className="relative border-2 border-dashed rounded-xl transition-all duration-200 border-gray-200 hover:border-gray-300"
                          >
                            <input
                              type="file"
                              accept=".pdf,.docx,.doc,.csv"
                              onChange={(e) => handleFileChange(e)}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              id="file-upload"
                            />
                            <label
                              htmlFor="file-upload"
                              className="flex flex-col items-center justify-center py-8 px-4 cursor-pointer"
                            >
                              <div className="p-3 bg-gray-100 rounded-2xl mb-3">
                                <Upload className="h-6 w-6 text-black" />
                              </div>
                              <p className="text-sm font-medium text-gray-700 mb-1">
                                Drop your file here or click to browse
                              </p>
                              <p className="text-xs text-gray-500">
                                Supports PDF, DOCX, and CSV files
                              </p>
                            </label>
                          </div>
                          <div className="relative py-6">
                            <div className="absolute inset-0 flex items-center">
                              <div className="w-full border-t border-gray-300"></div>
                            </div>
                            <div className="relative flex justify-center">
                              <span className="px-4 py-1 bg-white text-gray-600 text-sm font-semibold tracking-wide rounded-full shadow-sm">
                                OR PASTE CONTENT
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="border border-gray-200 rounded-xl p-4 bg-white">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`p-3 ${typeInfo.bg} rounded-xl`}>
                                <TypeIcon className={`h-5 w-5 ${typeInfo.color}`} />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {uploadForm.file.name}
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {(uploadForm.file.size / 1024).toFixed(1)} KB • {typeInfo.label}
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={clearFile}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors group"
                            >
                              <X className="h-4 w-4 text-gray-400 group-hover:text-black" />
                            </button>
                          </div>
                        </div>
                      )
                    )
                  }

                  {
                    !uploadForm.file && uploadForm.contentType !== 'url' && (
                      <>
                        <textarea
                          value={uploadForm.content}
                          onChange={(e) => handleContentChange(e)}
                          rows={6}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-gray-500/20 focus:border-gray-500 transition-all duration-200 resize-none text-gray-900 placeholder:text-gray-400"
                          placeholder="Paste text content here"
                        />
                        <div className="relative py-6">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300"></div>
                          </div>
                          <div className="relative flex justify-center">
                            <span className="px-4 py-1 bg-white text-gray-600 text-sm font-semibold tracking-wide rounded-full shadow-sm">
                              OR PASTE URL
                            </span>
                          </div>
                        </div>
                      </>
                    )
                  }
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    className="px-6 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploading || (!uploadForm.content && !uploadForm.file)}
                    className="px-6 py-3 text-sm font-semibold text-white bg-black rounded-xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2 shadow-lg"
                  >
                    {uploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" />
                        <span>Upload & Generate FAQs</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {
        showImportModal && (
          <div className="fixed inset-0 overflow-hidden z-50">
            <div
              className="fixed inset-0 bg-gradient-to-br from-gray-900/40 via-gray-900/30 to-gray-900/40 backdrop-blur-sm transition-opacity"
              onClick={() => setShowImportModal(false)}
            />

            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-100 w-full max-w-5xl mx-auto">
                <div className="px-6 py-5 border-b border-gray-100 bg-gray-50 rounded-t-3xl">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <Globe className="h-6 w-6 text-black" />
                        Select URLs for FAQ Generation
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Choose URLs from your sitemaps to generate comprehensive FAQs
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowManualAdd(!showManualAdd)}
                        disabled={discovering || loadingManualSitemap}
                        className="px-4 py-2 text-sm font-medium text-white bg-black rounded-xl hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2 shadow-sm hover:shadow-md transition-all"
                      >
                        <Plus className="h-4 w-4" />
                        Add Sitemap
                      </button>
                      <button
                        type="button"
                        onClick={handleDeleteSelectedUrls}
                        disabled={!selectedUrls.size}
                        className="px-4 py-2 text-sm font-medium text-white bg-black rounded-xl hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2 shadow-sm hover:shadow-md transition-all"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={refreshSitemapDiscovery}
                        disabled={discovering || loadingManualSitemap}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 shadow-sm hover:shadow-md transition-all"
                      >
                        {discovering ? "Refreshing..." : "Refresh"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  {showManualAdd && (
                    <div className="bg-gray-50 rounded-2xl p-5 border-2 border-gray-200 shadow-sm">
                      <div className="space-y-3">
                        <div className="flex items-start gap-2">
                          <FileText className="h-5 w-5 text-black mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <label className="block text-sm font-semibold text-gray-800 mb-2">
                              Enter Sitemap URL
                            </label>
                            <input
                              type="url"
                              placeholder="https://example.com/sitemap.xml"
                              value={manualSitemapUrl}
                              onChange={(e) => setManualSitemapUrl(e.target.value)}
                              disabled={loadingManualSitemap}
                              className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-gray-500 disabled:opacity-50 transition-all bg-white"
                            />
                            <p className="text-xs text-gray-600 mt-2 flex items-center gap-1">
                              <span className="inline-block w-1 h-1 bg-black rounded-full" />
                              Enter the full URL to a sitemap.xml file
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleManualSitemapSubmit}
                            disabled={!manualSitemapUrl.trim() || loadingManualSitemap}
                            className="px-5 py-2.5 text-sm font-medium text-white bg-black rounded-xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
                          >
                            {loadingManualSitemap ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                <span>Loading...</span>
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4" />
                                <span>Load Sitemap</span>
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowManualAdd(false);
                              setManualSitemapUrl('');
                            }}
                            disabled={loadingManualSitemap}
                            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-all"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {!discovering && !loadingManualSitemap && totalUrls > 0 && (
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search URLs... (e.g., /blog, products, support)"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full px-5 py-3 pl-12 text-sm border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all bg-white"
                      />
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      {searchQuery && (
                        <button
                          type="button"
                          onClick={() => setSearchQuery('')}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1 transition-all"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}

                  {!discovering && !loadingManualSitemap && totalUrls > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl border border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-black rounded-full animate-pulse" />
                        <span className="text-sm font-medium text-gray-700">
                          {isSearching ? (
                            <>Showing <span className="font-bold text-black">{totalUrls}</span> matching URLs</>
                          ) : (
                            <>{totalUrls} total URLs available</>
                          )}
                        </span>
                      </div>
                      <span className="text-sm text-gray-600">
                        <span className="font-semibold text-black">{selectedUrls.size}</span> selected
                      </span>
                    </div>
                  )}

                  {(discovering || loadingManualSitemap) && (
                    <div className="flex flex-col items-center justify-center py-16 space-y-4">
                      <div className="relative">
                        <div className="w-16 h-16 border-4 border-gray-200 rounded-full" />
                        <div className="w-16 h-16 border-4 border-black border-t-transparent rounded-full animate-spin absolute top-0" />
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-semibold text-gray-800">
                          {loadingManualSitemap ? 'Loading sitemap...' : 'Discovering sitemap URLs...'}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">This may take a moment</p>
                      </div>
                    </div>
                  )}

                  {!discovering && !loadingManualSitemap && totalUrls === 0 && (
                    <div className="text-center py-16 space-y-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                        <Globe className="h-8 w-8 text-gray-400" />
                      </div>
                      <div>
                        <p className="text-base font-medium text-gray-800 mb-2">No URLs Found</p>
                        <p className="text-sm text-gray-600 max-w-md mx-auto">
                          No URLs found from sitemap. Ensure your organization domain has a valid sitemap.xml or use &quot;Add Sitemap&quot; to enter a custom URL.
                        </p>
                      </div>
                    </div>
                  )}

                  {!discovering && !loadingManualSitemap && totalUrls > 0 && (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                      {isSearching ? (
                        Object.keys(filteredGroupedUrls).length === 0 ? (
                          <div className="text-center py-16 space-y-4">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                              <Search className="h-8 w-8 text-gray-400" />
                            </div>
                            <div>
                              <p className="text-base font-medium text-gray-800 mb-2">No Results Found</p>
                              <p className="text-sm text-gray-600">
                                No URLs match your search. Try a different query.
                              </p>
                            </div>
                          </div>
                        ) : (
                          Object.keys(filteredGroupedUrls).map((group) => {
                            const urls = filteredGroupedUrls[group];
                            const groupKey = `search-${group}`;

                            return (
                              <div key={groupKey} className="border-2 border-gray-200 rounded-2xl overflow-hidden hover:border-black transition-all shadow-sm hover:shadow-md">
                                <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                                  <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <input
                                      type="checkbox"
                                      checked={groupAllChecked(urls)}
                                      onChange={() => toggleGroup(urls)}
                                      className="h-5 w-5 text-black border-2 border-gray-300 rounded focus:ring-2 focus:ring-gray-500 cursor-pointer flex-shrink-0"
                                    />
                                    <button
                                      type="button"
                                      className="text-left flex items-center gap-2 min-w-0 flex-1"
                                      onClick={() => toggleGroupExpand(groupKey)}
                                    >
                                      <Folder className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                      <span className="font-semibold text-gray-900 truncate">
                                        {group}
                                      </span>
                                      <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-black rounded-full flex-shrink-0">
                                        {urls.length}
                                      </span>
                                    </button>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => toggleGroupExpand(groupKey)}
                                    className="text-gray-500 hover:text-gray-700 hover:bg-gray-200 p-1.5 rounded-lg transition-all flex-shrink-0 ml-2"
                                  >
                                    {groupExpanded[groupKey] ?
                                      <ChevronUp className="h-5 w-5" /> :
                                      <ChevronDown className="h-5 w-5" />
                                    }
                                  </button>
                                </div>

                                {groupExpanded[groupKey] && (
                                  <div className="divide-y divide-gray-100 bg-white">
                                    {urls.map((url, urlIndex) => (
                                      <label
                                        key={`${groupKey}-${urlIndex}`}
                                        className={`flex items-center gap-3 py-3 px-4 transition-all ${isUrlProcessed(url)
                                          ? 'opacity-50 cursor-not-allowed bg-gray-50'
                                          : 'cursor-pointer hover:bg-gray-50'
                                          }`}
                                        title={isUrlProcessed(url) ? 'FAQs already generated for this URL' : ''}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isChecked(url)}
                                          onChange={() => toggleUrl(url)}
                                          disabled={isUrlProcessed(url)}
                                          className="h-4 w-4 text-black border-2 border-gray-300 rounded focus:ring-2 focus:ring-gray-500 flex-shrink-0"
                                        />
                                        <span className="text-sm text-gray-700 break-all flex-1 font-mono">
                                          {toDisplayPath(url)}
                                        </span>
                                        {isUrlProcessed(url) && (
                                          <span className="text-[10px] uppercase tracking-wider bg-gray-100 text-black px-2.5 py-1 rounded-full flex-shrink-0 font-semibold">
                                            ✓ Processed
                                          </span>
                                        )}
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )
                      ) : (
                        // Hierarchical view - Sitemap > Groups > URLs
                        filteredGroupedUrls.map((sitemapGroups, sitemapIndex) => {
                          const sitemap = mainSitemapUrls[sitemapIndex];
                          const sitemapKey = `sitemap-${sitemapIndex}`;
                          const groupKeys = Object.keys(sitemapGroups);
                          const totalSitemapUrls = Object.values(sitemapGroups).reduce((sum, urls) => sum + urls.length, 0);

                          return (
                            <div key={sitemapKey} className="border-2 border-gray-200 rounded-2xl overflow-hidden shadow-md">
                              {/* Sitemap Header */}
                              <div className="bg-black px-4 py-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <button
                                      type="button"
                                      onClick={() => toggleSitemapExpand(sitemapIndex)}
                                      className="text-white hover:bg-white/20 p-1.5 rounded-lg transition-all flex-shrink-0"
                                    >
                                      {sitemapExpanded[sitemapIndex] ?
                                        <ChevronUp className="h-5 w-5" /> :
                                        <ChevronDown className="h-5 w-5" />
                                      }
                                    </button>
                                    <Globe className="h-5 w-5 text-white flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="font-bold text-white truncate">{sitemap?.title || 'Sitemap'}</p>
                                      <p className="text-xs text-gray-400 truncate">{sitemap?.original_url || ''}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                    <span className="px-3 py-1 text-xs font-bold bg-gray-800 text-white rounded-full backdrop-blur-sm">
                                      {groupKeys.length} groups
                                    </span>
                                    <span className="px-3 py-1 text-xs font-bold bg-gray-800 text-white rounded-full backdrop-blur-sm">
                                      {totalSitemapUrls} URLs
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Groups within Sitemap */}
                              {sitemapExpanded[sitemapIndex] && (
                                <div className="bg-white p-3 space-y-2">
                                  {groupKeys.map((group) => {
                                    const urls = sitemapGroups[group];
                                    const groupKey = `${sitemapKey}-${group}`;

                                    return (
                                      <div key={groupKey} className="border border-gray-200 rounded-xl overflow-hidden hover:border-black transition-all">
                                        <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50">
                                          <div className="flex items-center gap-3 min-w-0 flex-1">
                                            <input
                                              type="checkbox"
                                              checked={groupAllChecked(urls)}
                                              onChange={() => toggleGroup(urls)}
                                              className="h-4 w-4 text-black border-2 border-gray-300 rounded focus:ring-2 focus:ring-gray-500 cursor-pointer flex-shrink-0"
                                            />
                                            <button
                                              type="button"
                                              className="text-left flex items-center gap-2 min-w-0 flex-1"
                                              onClick={() => toggleGroupExpand(groupKey)}
                                            >
                                              <Folder className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                              <span className="font-medium text-gray-800 truncate text-sm">
                                                {group}
                                              </span>
                                              <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-black rounded-full flex-shrink-0">
                                                {urls.length}
                                              </span>
                                            </button>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => toggleGroupExpand(groupKey)}
                                            className="text-gray-500 hover:text-gray-700 hover:bg-gray-200 p-1 rounded-lg transition-all flex-shrink-0 ml-2"
                                          >
                                            {groupExpanded[groupKey] ?
                                              <ChevronUp className="h-4 w-4" /> :
                                              <ChevronDown className="h-4 w-4" />
                                            }
                                          </button>
                                        </div>

                                        {groupExpanded[groupKey] && (
                                          <div className="divide-y divide-gray-100 bg-white">
                                            {urls.map((url, urlIndex) => (
                                              <label
                                                key={`${groupKey}-${urlIndex}`}
                                                className={`flex items-center gap-2.5 py-2.5 px-3 transition-all ${isUrlProcessed(url)
                                                  ? 'opacity-50 cursor-not-allowed bg-gray-50'
                                                  : 'cursor-pointer hover:bg-gray-50'
                                                  }`}
                                                title={isUrlProcessed(url) ? 'FAQs already generated for this URL' : ''}
                                              >
                                                <input
                                                  type="checkbox"
                                                  checked={isChecked(url)}
                                                  onChange={() => toggleUrl(url)}
                                                  disabled={isUrlProcessed(url)}
                                                  className="h-3.5 w-3.5 text-black border-2 border-gray-300 rounded focus:ring-2 focus:ring-gray-500 flex-shrink-0"
                                                />
                                                <span className="text-xs text-gray-700 break-all flex-1 font-mono">
                                                  {toDisplayPath(url)}
                                                </span>
                                                {isUrlProcessed(url) && (
                                                  <span className="text-[9px] uppercase tracking-wider bg-gray-100 text-black px-2 py-0.5 rounded-full flex-shrink-0 font-semibold">
                                                    ✓
                                                  </span>
                                                )}
                                              </label>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>

                {/* Footer */}
                {!discovering && !loadingManualSitemap && totalUrls > 0 && (
                  <div className="px-6 py-5 border-t border-gray-200 bg-gray-50 rounded-b-3xl">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={handleSelectAll}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={handleClearAll}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
                        >
                          Clear All
                        </button>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-2">
                        <button
                          type="button"
                          onClick={() => setShowImportModal(false)}
                          className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-all order-2 sm:order-1"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleQueueSelectedUrl}
                          disabled={selectedUrls.size === 0 || queuing}
                          className="px-6 py-2.5 text-sm font-medium text-white bg-black rounded-xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg order-1 sm:order-2"
                        >
                          {queuing ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              <span>Queuing...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4" />
                              <span>
                                Generate Knowledge Vault ({selectedUrls.size})
                              </span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }
    </div>
  );
}

export default KnowledgeVault;
